import { useCallback, useEffect, useId, useMemo, useState } from "react";
import "@/App.css";
import { applyBrand } from "./lib/brand";
import {
  Activity, AlertCircle, ArrowUpRight, Bell, Building2, CalendarDays, Check, ChevronRight,
  CircleHelp, FileUp, Filter, Inbox, LayoutDashboard, LogOut, MoreHorizontal,
  Mail, Plus, RefreshCw, ScanSearch, Search, Send, Settings2, SlidersHorizontal, Sparkles,
  FileText, Headphones, LifeBuoy, Megaphone, Phone, PhoneCall, Target, Trash2, TrendingUp, Upload, Users, X, Zap, Link2,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import ApolloDiscoveryModal from "./components/ApolloDiscoveryModal";
import ImportView from "./components/ImportView";
import MailboxModal from "./components/MailboxModal";
import TargetProfiles from "./components/TargetProfiles";
import FitChip from "./components/FitChip";
import ConnectCrmModal from "./components/ConnectCrmModal";
import { useListView, ListBar } from "./components/ListSearch";
import AccountsModal from "./components/AccountsModal";
import Login from "./components/Login";
import SetPassword from "./components/SetPassword";
import Setup from "./components/Setup";
import SettingsModal from "./components/SettingsModal";
import CallsView from "./components/CallsView";
import SalesCalls from "./components/SalesCalls";
import FollowupsToday from "./components/FollowupsToday";
import SupportDesk from "./components/SupportDesk";
import ProspectDetails from "./components/ProspectDetails";
import AddContactModal from "./components/AddContactModal";
import ComposeEmail from "./components/ComposeEmail";
import SetupWizard from "./components/SetupWizard";
import SocialView from "./components/SocialView";
import {
  getConfig, getTarget, saveTarget, listProspects, approveProspect, deleteProspect,
  getToken, clearToken, setOnUnauthorized, getSetupStatus, LOGO_URL,
  listAccounts, deleteAccount, scanAccount, verifyProspectEmail,
  getMessages, saveMessage, resetMessage, markMessageSent, sendMessageNow,
  launchSequence, stopSequence, resetSequence, getSequencerStatus, getTodoList, dismissNewsLead,
  setLinkedinUrl, connectLinkedinNow, getReplyRateAnalytics, getSegments, setSegmentAlertFeed,
  markCallTodoDone, listFields, getContactSettings,
  followupsCount,
} from "./lib/api";

function Badge({ children, tone = "neutral" }) { const id = useId(); return <span className={`badge badge-${tone}`} data-testid={`badge-${String(children).toLowerCase().replaceAll(" ", "-")}-${id.replaceAll(":", "")}`}>{children}</span>; }
function Avatar({ prospect }) { return <div className={`avatar avatar-${prospect.color || "blue"}`} data-testid={`prospect-avatar-${prospect.id}`}>{prospect.initials || "?"}</div>; }
const SENDABLE_EMAIL = ["verified", "phone_confirmed", "manual", "unverified"]; // statuses a contact can be approved and emailed with
const EMAIL_LABELS = { phone_confirmed: "confirmed on call", manual: "entered by you" };
function EmailPill({ status }) { const label = status || "unavailable"; return <span className={`email-pill email-${label}`} data-testid={`email-status-${label}`} title={label === "phone_confirmed" ? "The person gave or confirmed this address on a call. Verify it first if you want to be sure." : undefined}>{SENDABLE_EMAIL.includes(label) ? <Check size={11} /> : <Sparkles size={11} />}{EMAIL_LABELS[label] || label}</span>; }


function Sidebar({ active, setActive, onLogout, brand, onSettings, due }) {
  const CURRENT_USER_NAME = brand.user_name || "You";
  const items = [["Overview", LayoutDashboard], ["To-do", CalendarDays], ["Contacts", Users], ["Companies", Building2], ["Import", FileUp], ["Sequences", Send], ["Calls", PhoneCall], ["Outbound Sales Calls", Headphones], ["Support Desk", LifeBuoy], ["Social", Megaphone], ["Analytics", TrendingUp], ["Targeting", Target]];
  return <aside className="sidebar">
    <div className="brand">{brand.has_logo ? <img src={`${LOGO_URL}?v=${brand.logo_v || 0}`} alt={brand.company_name} className="brand-logo" /> : <span>{brand.company_name}</span>}</div>
    <div className="workspace-switcher" data-testid="workspace-switcher"><div className="workspace-dot">{(brand.company_name || "?").slice(0, 1).toUpperCase()}</div><div><b>{brand.company_name}</b><small>Outbound workspace</small></div><ChevronRight size={15} /></div>
    <nav className="nav-list" aria-label="Main navigation">{items.map(([label, Icon]) => <button key={label} className={`nav-item ${active === label ? "active" : ""}`} onClick={() => setActive(label)} data-testid={`nav-${label.toLowerCase().replaceAll(" ", "-").replaceAll("&", "and")}`}><Icon size={17} /><span>{label}</span>{label === "Outbound Sales Calls" && due > 0 && <em className="nav-badge" title="Follow-ups due" data-testid="nav-due">{due}</em>}</button>)}</nav>
    <div className="sidebar-bottom"><div className="health"><span className="pulse-dot" />All systems healthy<CircleHelp size={14} /></div><button className="nav-item" onClick={onSettings} data-testid="nav-settings"><Settings2 size={17} />Settings</button><button className="nav-item" onClick={onLogout} data-testid="nav-logout"><LogOut size={17} />Sign out</button><div className="user-card"><div className="user-avatar">{CURRENT_USER_NAME.slice(0, 2).toUpperCase()}</div><div><b>{CURRENT_USER_NAME}</b><small>Sales owner</small></div><MoreHorizontal size={16} /></div></div>
  </aside>;
}

function Topbar({ active, onImport, onDiscover, apolloConfigured, brand, search, onSearch }) {
  const CURRENT_USER_NAME = brand.user_name || "there";
  return <header className="topbar">
    <div><div className="crumb">{brand.company_name} <ChevronRight size={13} /> {active}</div><h1>{active === "Overview" ? `Good morning, ${CURRENT_USER_NAME}` : active}</h1></div>
    <div className="top-actions">
      <div className="search"><Search size={16} /><input data-testid="global-search-input" value={search} onChange={e => onSearch(e.target.value)} placeholder="Search contacts…" aria-label="Search contacts" /></div>
      <button className="icon-button" data-testid="notifications-button" onClick={() => toast.info("You're all caught up")}><Bell size={17} /><span className="notification-dot" /></button>
      {apolloConfigured && <button className="outline-button" data-testid="discover-apollo-button" onClick={onDiscover}><Zap size={15} /> Discover</button>}
      <button className="outline-button" data-testid="import-prospects-button" onClick={onImport}><Upload size={15} /> Import</button>
    </div>
  </header>;
}

function KpiStrip({ prospects }) {
  const pending = prospects.filter(p => p.status === "Needs approval").length;
  const approved = prospects.filter(p => p.status === "Approved").length;
  const active = prospects.filter(p => p.sequence_status === "active").length;
  const replied = prospects.filter(p => p.sequence_status === "replied").length;
  return <div className="kpi-grid">
    <div className="kpi"><div className="kpi-label">Contacts</div><strong>{prospects.length}</strong><small>{pending} waiting for review</small></div>
    <div className="kpi"><div className="kpi-label">Approved</div><strong>{approved}</strong><small>Ready for a sequence</small></div>
    <div className="kpi"><div className="kpi-label">In a sequence</div><strong>{active}</strong><small>Emails going out</small></div>
    <div className="kpi"><div className="kpi-label">Replied</div><strong>{replied}</strong><small>Sequence stopped on reply</small></div>
  </div>;
}

const GRADE_RANK = { A: 5, B: 4, C: 3, D: 2, "?": 1, X: 0 };
const fitRank = (p) => p.fit ? GRADE_RANK[p.fit.grade] * 1000 + p.fit.score : -1;

function ApprovalCard({ prospects, onApprove, onDiscover, apolloConfigured }) {
  const [page, setPage] = useState(0);
  const pending = prospects.filter(p => p.status === "Needs approval").sort((a, b) => fitRank(b) - fitRank(a));
  return <section className="approval-section">
    <div className="section-heading"><div><div className="eyebrow"><span className="eyebrow-dot" /> HUMAN CHECKPOINT</div><h2>Review new contacts</h2><p>Review the leads found before any credits are spent or sequences launch.</p></div><button className="text-button" onClick={() => toast.info("Showing all prospects")} data-testid="view-all-prospects-button">View all <ArrowUpRight size={15} /></button></div>
    <div className="approval-card">
      <div className="approval-card-head">
        <div className="approval-count"><span>{pending.length}</span><div><b>Contacts waiting for approval</b><small>Newest first</small></div></div>
        <div className="approval-meta"><span><span className="green-dot" />0 credits spent</span>{apolloConfigured && <button className="ghost-button" data-testid="refresh-prospect-scoring" onClick={onDiscover}><RefreshCw size={14} /> Run Apollo discovery</button>}</div>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th className="check-col"><input type="checkbox" data-testid="select-all-prospects" /></th><th>Contact</th><th>Location</th><th>Signals</th><th>Email</th><th>Action</th></tr></thead>
          <tbody>
            {pending.length === 0 && <tr><td colSpan={6} className="empty-row" data-testid="empty-prospects"><Sparkles size={16} /> No contacts yet — import your customers or run a lead search.</td></tr>}
            {pending.slice(page * 5, page * 5 + 5).map(p => <tr key={p.id} data-testid={`prospect-row-${p.id}`}><td><input type="checkbox" data-testid={`select-prospect-${p.id}`} /></td><td><div className="person-cell"><Avatar prospect={p} /><div><b>{p.name}</b><small>{p.role} · {p.company}</small></div></div></td><td><small>{p.location || "—"}</small></td><td><div className="signal-list">{(p.signals || []).map(s => <Badge key={s} tone={s === "Hiring" ? "hot" : s === "Growth" ? "green" : "blue"}>{s}</Badge>)}{(!p.signals || p.signals.length === 0) && <small className="muted">—</small>}</div></td><td><small>{p.email || "—"}</small></td><td><button className="approve-button" onClick={() => onApprove(p.id)} disabled={!SENDABLE_EMAIL.includes(p.email_status)} title={!SENDABLE_EMAIL.includes(p.email_status) ? "No usable email for this contact" : undefined} data-testid={`approve-prospect-${p.id}`}><Check size={14} /> Approve</button></td></tr>)}
          </tbody>
        </table>
      </div>
      <div className="approval-foot"><span>Showing {pending.length ? page * 5 + 1 : 0}–{Math.min(page * 5 + 5, pending.length)} of {pending.length} contacts</span><span className="pager"><button className="pagination-button" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="prev-prospects-page">Previous</button><button className="pagination-button" disabled={(page + 1) * 5 >= pending.length} onClick={() => setPage(page + 1)} data-testid="next-prospects-page">Next <ChevronRight size={14} /></button></span></div>
    </div>
  </section>;
}


function Overview({ prospects, onApprove, setActive, onDiscover, apolloConfigured, onLog }) {
  return <>
    <FollowupsToday compact onLog={onLog} />
    <div className="status-banner"><div className="status-icon"><Sparkles size={17} /></div><div><b>Nothing goes out without your approval</b><span>{prospects.filter(p => p.status === "Needs approval").length} contacts waiting for review</span></div><button onClick={() => setActive("Contacts")} data-testid="review-prospects-banner-button">Review prospects <ArrowUpRight size={15} /></button><X size={16} className="dismiss" /></div>
    <KpiStrip prospects={prospects} />
    <ApprovalCard prospects={prospects} onApprove={onApprove} onDiscover={onDiscover} apolloConfigured={apolloConfigured} />
  </>;
}

function formatCustom(f, v) { if (v === undefined || v === null || v === "") return "—"; return f.type === "yes_no" ? (v ? "Yes" : "No") : String(v); }

function ProspectsView({ onCall, onRefresh, prospects, onApprove, onDelete, onImport, onDiscover, onVerify, verifyingId, onOpenSequence, apolloConfigured, onUpdateProspect, onAddProspect, query, setQuery }) {
  const [adding, setAdding] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [mailbox, setMailbox] = useState(false);
  const [composeId, setComposeId] = useState(null);
  const [needsApproval, setNeedsApproval] = useState(false);
  const [tab, setTab] = useState("all");
  const [fitFilter, setFitFilter] = useState("any");
  const [fitFirst, setFitFirst] = useState(true);
  const [fields, setFields] = useState([]);
  const [detailId, setDetailId] = useState(null);
  useEffect(() => { listFields().then(r => setFields(r.fields)).catch(() => {}); getContactSettings().then(r => setNeedsApproval(r.manual_requires_approval)).catch(() => {}); }, []);
  const columns = fields.filter(f => f.show_in_table);
  const detail = prospects.find(p => p.id === detailId);
  const composing = prospects.find(p => p.id === composeId);
  const filtered = prospects.filter(p => {
    const hay = `${p.name} ${p.company} ${p.role} ${p.location || ""} ${p.email || ""} ${p.phone || ""}`.toLowerCase();
    const digits = (p.phone || "").replace(/\D/g, "");
    const q = query.toLowerCase().split(/[\s,]+/).filter(Boolean).every(t => hay.includes(t) || (/^\+?[\d()-]+$/.test(t) && digits.includes(t.replace(/\D/g, ""))));  // words or phone digits, like a dialer search  // every word must match, so "sydney nsw" or "ana harbor" both work
    if (!q) return false;
    if (fitFilter !== "any") { const g = p.fit?.grade || "none"; if (fitFilter === "good" ? !["A", "B"].includes(g) : fitFilter === "weak" ? !["C", "D"].includes(g) : g !== fitFilter) return false; }
    if (tab === "approval") return p.status === "Needs approval";
    if (tab === "nurture") return p.status === "Nurture";
    if (tab === "calls") return p.source === "call";
    return true;
  });
  const [showAll, setShowAll] = useState(false);
  const hasFit = prospects.some(p => p.fit);
  if (hasFit && fitFirst) filtered.sort((a, b) => fitRank(b) - fitRank(a));
  const shownRows = query.trim() || showAll ? filtered : filtered.slice(0, 25);
  return <div className="page-view">
    <div className="view-toolbar">
      <div><h2>Contacts</h2><p>Everyone you've imported or found. Approve a contact to start a sequence.</p></div>
      <div className="toolbar-actions">
        <button className="outline-button" data-testid="filter-prospects-button"><Filter size={15} /> Filters</button>
        {apolloConfigured && <button className="outline-button" data-testid="discover-apollo-button-2" onClick={onDiscover}><Zap size={15} /> Lead search</button>}
        <button className="outline-button" onClick={() => setAdding(true)} data-testid="prospect-add-button"><Plus size={15} /> Add contact</button>
        <button className="outline-button" onClick={() => setMailbox(true)} data-testid="prospect-mailbox-button"><Mail size={15} /> Find in mailbox</button>
        <button className="outline-button" onClick={() => setConnecting(true)} data-testid="prospect-connect-crm-button"><Link2 size={15} /> Connect CRM</button>
        <button className="primary-button" onClick={onImport} data-testid="prospect-import-button"><Upload size={15} /> Import</button>
      </div>
    </div>
    <div className="contact-search">
      <Search size={20} />
      <input data-testid="prospect-search-input" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by name, company, phone number, address or email" aria-label="Search contacts" autoFocus />
      {query && <button className="contact-search-clear" onClick={() => setQuery("")} aria-label="Clear search"><X size={18} /></button>}
    </div>
    <small className="muted contact-count">{query.trim() ? `${filtered.length} of ${prospects.length} contacts match` : `${filtered.length} contacts`}</small>
    <div className="filter-row">
      {prospects.some(p => p.fit) && <><select className="fit-filter" value={fitFilter} onChange={e => setFitFilter(e.target.value)} aria-label="Filter by fit" data-testid="fit-filter">
        <option value="any">Any fit</option><option value="good">Great and good fit (A, B)</option><option value="weak">Possible and weak (C, D)</option><option value="?">Not enough data</option><option value="X">Not a fit</option></select>
        <label className="check-inline"><input type="checkbox" checked={fitFirst} onChange={e => setFitFirst(e.target.checked)} /> Best fit first</label></>}
      <button onClick={() => setTab("all")} className={tab === "all" ? "active-tab" : ""} data-testid="filter-all-button"><Badge tone={tab === "all" ? "blue" : "neutral"}>All {prospects.length}</Badge></button>
      <button onClick={() => setTab("approval")} className={tab === "approval" ? "active-tab" : ""} data-testid="filter-approval-button">Needs approval <span>{prospects.filter(p => p.status === "Needs approval").length}</span></button>
      <button onClick={() => setTab("nurture")} className={tab === "nurture" ? "active-tab" : ""} data-testid="filter-nurture-button">Nurture <span>{prospects.filter(p => p.status === "Nurture").length}</span></button>
      <button onClick={() => setTab("calls")} className={tab === "calls" ? "active-tab" : ""} data-testid="filter-calls-button">From calls <span>{prospects.filter(p => p.source === "call").length}</span></button>
    </div>
    <div className="full-table panel">
      <table>
        <thead><tr><th><input type="checkbox" data-testid="prospects-select-all" /></th><th>Contact</th><th>Company</th><th>Email</th>{columns.map(f => <th key={f.key}>{f.label}</th>)}<th>Fit</th><th>Signals</th><th>Status</th><th /></tr></thead>
        <tbody>
          {filtered.length === 0 && <tr><td colSpan={8 + columns.length} className="empty-row" data-testid="empty-queue">No contacts match. Import your customers or run a lead search.</td></tr>}
          {shownRows.map(p => <tr key={p.id} data-testid={`queue-row-${p.id}`}>
            <td><input type="checkbox" data-testid={`queue-select-${p.id}`} /></td>
            <td><div className="person-cell"><Avatar prospect={p} /><div><b><button className="link-button" onClick={() => setDetailId(p.id)} data-testid={`queue-open-${p.id}`}>{p.name}</button></b><small>{p.role}</small></div></div></td>
            <td><b>{p.company}</b><small className="table-sub">{p.location}</small></td>
            <td><small>{p.email || "—"}</small></td>
            {columns.map(f => <td key={f.key}><small>{formatCustom(f, p.custom?.[f.key])}</small></td>)}
            <td><FitChip fit={p.fit} /></td>
            <td><div className="signal-list">{(p.signals || []).map(s => <Badge key={s} tone={s === "Hiring" ? "hot" : "blue"}>{s}</Badge>)}<Badge tone="neutral">{p.source}</Badge></div></td>
            <td><Badge tone={p.status === "Approved" ? "green" : p.status === "Nurture" ? "neutral" : "yellow"}>{p.status}</Badge></td>
            <td className="row-actions">
              {p.status === "Needs approval" && <button className="approve-button" onClick={() => onApprove(p.id)} disabled={!SENDABLE_EMAIL.includes(p.email_status)} title={!SENDABLE_EMAIL.includes(p.email_status) ? "No usable email for this contact" : undefined} data-testid={`queue-approve-${p.id}`}><Check size={14} /> Approve</button>}
              {p.phone && onCall && <button className="icon-button small" title="Log a call with this contact" onClick={() => onCall(p)} data-testid={`queue-call-${p.id}`}><Phone size={14} /></button>}
              {p.email && p.email_status !== "bounced" && <button className="icon-button small" title="Write an email to this contact" onClick={() => setComposeId(p.id)} data-testid={`queue-compose-${p.id}`}><Mail size={14} /></button>}
              {p.status === "Approved" && <button className="outline-button" onClick={() => onOpenSequence(p.id)} data-testid={`queue-sequence-${p.id}`}><Send size={14} /> Sequence</button>}
              <button className="icon-button small" title="Delete" onClick={() => onDelete(p.id)} data-testid={`queue-delete-${p.id}`}><Trash2 size={14} /></button>
            </td>
          </tr>)}
        </tbody>
      </table>
      {!query.trim() && filtered.length > 25 && <div className="list-bar"><small className="muted">Showing {shownRows.length} of {filtered.length} contacts. Use the search box above to find one.</small>
        <button className="link-button" onClick={() => setShowAll(!showAll)}>{showAll ? "Show fewer" : `Show all ${filtered.length}`}</button></div>}
      
    </div>
    {composing && <ComposeEmail prospect={composing} close={() => setComposeId(null)} onSent={(u) => onUpdateProspect(composing.id, u)} />}
    {mailbox && <MailboxModal close={() => setMailbox(false)} onAdded={onRefresh} />}
    {connecting && <ConnectCrmModal close={() => setConnecting(false)} onImported={onRefresh} onOpenImport={onImport} />}
    {adding && <AddContactModal fields={fields} requiresApproval={needsApproval} close={() => setAdding(false)} onAdded={onAddProspect} />}
    {detail && <ProspectDetails key={detail.id} prospect={detail} fields={fields} close={() => setDetailId(null)} onSaved={(u) => onUpdateProspect(detail.id, u)} />}
  </div>;
}

function SequencePicker({ prospects, onSelect }) {
  const approved = prospects.filter(p => p.status === "Approved");
  const view = useListView(approved, p => `${p.name} ${p.company} ${p.role} ${p.location || ""} ${p.email || ""}`, 10);
  return <div className="page-view">
    <div className="view-toolbar"><div><div className="eyebrow"><span className="eyebrow-dot" /> SEQUENCE BUILDER</div><h2>Pick a contact</h2><p>Choose an approved contact to draft or send their sequence.</p></div></div>
    <div className="full-table panel">
      <ListBar view={view} noun="contacts" placeholder="Search name, company, address, email…" />
      <table>
        <thead><tr><th>Contact</th><th>Company</th><th>Sequence</th><th /></tr></thead>
        <tbody>
          {approved.length === 0 && <tr><td colSpan={4} className="empty-row" data-testid="empty-sequence-picker">No approved contacts yet — approve someone in Contacts first.</td></tr>}
          {view.rows.map(p => <tr key={p.id} data-testid={`sequence-pick-row-${p.id}`}>
            <td><div className="person-cell"><Avatar prospect={p} /><div><b>{p.name}</b><small>{p.role}</small></div></div></td>
            <td>{p.company}</td>
            <td><Badge tone={SEQ_STATUS_TONE[p.sequence_status] || "neutral"}>{(p.sequence_status || "not_started").replace("_", " ")}</Badge>{p.messages_total > 0 && <small style={{ marginLeft: 6, color: "#4f6b5d" }}>{p.messages_sent || 0}/{p.messages_total}</small>}</td>
            <td><button className="primary-button" onClick={() => onSelect(p.id)} data-testid={`open-sequence-${p.id}`}>Open sequence</button></td>
          </tr>)}
        </tbody>
      </table>
    </div>
  </div>;
}

const SEQ_STATUS_TONE = { not_started: "neutral", active: "green", replied: "blue", completed: "blue", stopped: "yellow", bounced: "hot" };
const MSG_STATUS_LABEL = { draft: "Draft", scheduled: "Scheduled", sent: "Sent", stopped: "Stopped", failed: "Failed" };
const TASK_META = {
  linkedin_due: { label: "LinkedIn connect due", tone: "blue" },
  email_failed: { label: "Email needs attention", tone: "hot" },
  not_launched: { label: "Never started", tone: "yellow" },
  nurture: { label: "Worth re-engaging", tone: "purple" },
  news_lead: { label: "Local news mention", tone: "green" },
  call_demo_booked: { label: "Demo booked by phone", tone: "green" },
  call_callback_requested: { label: "Call back requested", tone: "blue" },
  call_send_info: { label: "Send info (from call)", tone: "blue" },
  social_reconnect: { label: "Reconnect social account", tone: "hot" },
};

function TodoView({ onOpenTask, onOpenCalls, onOpenSocial }) {
  const [tasks, setTasks] = useState(null);

  const load = () => getTodoList().then(setTasks).catch(() => toast.error("Could not load to-do list"));
  useEffect(() => { load(); }, []);

  const dismiss = async (leadId) => {
    try { await dismissNewsLead(leadId); setTasks(ts => ts.filter(t => t.lead_id !== leadId)); }
    catch (e) { toast.error("Could not dismiss"); }
  };

  const doneCall = async (callId) => {
    try { await markCallTodoDone(callId); setTasks(ts => ts.filter(t => t.call_id !== callId)); }
    catch (e) { toast.error("Could not update"); }
  };

  if (tasks === null) return <div className="page-view"><div className="panel" style={{ padding: 24 }}>Loading…</div></div>;

  return <div className="page-view">
    <div className="view-toolbar">
      <div><div className="eyebrow"><span className="eyebrow-dot" /> TODAY</div><h2>To-do</h2>
        <p>{tasks.length === 0 ? "Nothing needs your attention right now." : `${tasks.length} thing${tasks.length === 1 ? "" : "s"} need your attention`}</p>
      </div>
    </div>
    <div className="full-table panel">
      <table>
        <thead><tr><th>Prospect</th><th>Company</th><th>Task</th><th>Detail</th><th /></tr></thead>
        <tbody>
          {tasks.length === 0 && <tr><td colSpan={5} className="empty-row" data-testid="empty-todo"><Check size={16} /> All caught up.</td></tr>}
          {tasks.map((t, i) => {
            const meta = TASK_META[t.type] || { label: t.type, tone: "neutral" };
            return <tr key={`${t.prospect_id}-${t.lead_id || t.step_key || t.type}-${i}`} data-testid={`todo-row-${i}`}>
              <td><b>{t.name}</b></td>
              <td>{t.company}</td>
              <td><Badge tone={meta.tone}>{meta.label}</Badge></td>
              <td><small>{t.detail}</small></td>
              <td>
                {t.type === "social_reconnect"
                  ? <button className="primary-button" onClick={onOpenSocial} data-testid={`todo-open-${i}`}>Open Social <ChevronRight size={14} /></button>
                  : t.call_id
                  ? <div style={{ display: "flex", gap: 8 }}>
                      <button className="outline-button" onClick={onOpenCalls} data-testid={`todo-open-${i}`}>Open call</button>
                      <button className="outline-button" onClick={() => doneCall(t.call_id)} data-testid={`todo-done-${i}`}><Check size={14} /> Done</button>
                    </div>
                  : t.type === "news_lead"
                  ? <div style={{ display: "flex", gap: 8 }}>
                      <a className="outline-button" href={t.link} target="_blank" rel="noreferrer" data-testid={`todo-open-${i}`}>Read article</a>
                      <button className="outline-button" onClick={() => dismiss(t.lead_id)} data-testid={`todo-dismiss-${i}`}>Dismiss</button>
                    </div>
                  : <button className="primary-button" onClick={() => onOpenTask(t.prospect_id, t.step_key)} data-testid={`todo-open-${i}`}>Open <ChevronRight size={14} /></button>}
              </td>
            </tr>;
          })}
        </tbody>
      </table>
    </div>
  </div>;
}

const STEP_LABEL = { email_1: "Email 1", linkedin: "LinkedIn", email_2: "Email 2", email_3: "Email 3", email_4: "Email 4" };

function AnalyticsView() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    getReplyRateAnalytics().then(setRows).catch(() => toast.error("Could not load reply-rate analytics"));
  }, []);

  if (rows === null) return <div className="page-view"><div className="panel" style={{ padding: 24 }}>Loading…</div></div>;

  const totalSent = rows.reduce((n, r) => n + r.sent, 0);
  const totalReplied = rows.reduce((n, r) => n + r.replied, 0);
  const overallRate = totalSent ? Math.round((totalReplied / totalSent) * 1000) / 10 : 0;

  return <div className="page-view">
    <div className="view-toolbar">
      <div><div className="eyebrow"><span className="eyebrow-dot" /> WHAT'S WORKING</div><h2>Reply-rate analytics</h2>
        <p>{totalSent === 0 ? "No emails sent yet — this fills in as sequences go out." : `${totalSent} sent · ${totalReplied} replied · ${overallRate}% overall`}</p>
      </div>
    </div>
    <div className="full-table panel">
      <table>
        <thead><tr><th>Segment</th><th>Step</th><th>Subject actually sent</th><th>Sent</th><th>Replied</th><th>Reply rate</th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} className="empty-row" data-testid="empty-analytics">Nothing sent yet.</td></tr>}
          {rows.map((r, i) => <tr key={`${r.segment_id}-${r.step_key}-${i}`} data-testid={`analytics-row-${i}`}>
            <td>{r.segment_name}</td>
            <td><Badge tone="blue">{STEP_LABEL[r.step_key] || r.step_key}</Badge></td>
            <td><small>{r.subject}</small></td>
            <td>{r.sent}</td>
            <td>{r.replied}</td>
            <td>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div className="progress-line" style={{ width: 80, marginTop: 0 }}><span style={{ width: `${r.reply_rate}%` }} /></div>
                <b>{r.reply_rate}%</b>
              </div>
            </td>
          </tr>)}
        </tbody>
      </table>
    </div>
  </div>;
}

function SequencesView({ prospects, prospectId, initialStepKey, onSelectProspect, onUpdateProspect }) {
  const [view, setView] = useState(null);
  const [selected, setSelected] = useState(0);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [linkedinUrlInput, setLinkedinUrlInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sendStatus, setSendStatus] = useState(null);
  const [config, setConfig] = useState(null);

  const prospect = prospects.find(p => p.id === prospectId);

  useEffect(() => {
    if (!prospectId) return;
    setView(null); setSelected(0); setError("");
    getMessages(prospectId).then(v => {
      setView(v);
      if (initialStepKey) {
        const idx = v.messages.findIndex(m => m.step_key === initialStepKey);
        if (idx >= 0) setSelected(idx);
      }
    }).catch(() => toast.error("Could not load sequence"));
    getSequencerStatus().then(setSendStatus).catch(() => {});
    getConfig().then(setConfig).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospectId]);

  useEffect(() => { setLinkedinUrlInput(prospect?.linkedin_url || ""); }, [prospect]);

  useEffect(() => {
    const m = view?.messages?.[selected];
    if (m) { setSubject(m.subject); setBody(m.body); }
  }, [selected, view]);

  useEffect(() => { setError(""); }, [selected]);

  if (!prospectId || !prospect) {
    return <SequencePicker prospects={prospects} onSelect={onSelectProspect} />;
  }

  if (!view) return <div className="page-view"><div className="panel" style={{ padding: 24 }}>Loading sequence…</div></div>;

  const current = view.messages[selected];
  const editable = current && current.status === "draft";
  const STEP_LABEL = { email_1: "Email 1", linkedin: "LinkedIn connect", email_2: "Email 2", email_3: "Email 3" };
  const sentCount = view.messages.filter(m => m.status === "sent").length;
  const nextStep = view.messages.find(m => m.status !== "sent" && m.status !== "stopped");
  const progressLabel = sentCount === view.messages.length
    ? `All ${view.messages.length} touches sent`
    : nextStep
      ? `${sentCount}/${view.messages.length} sent · next up: ${STEP_LABEL[nextStep.step_key]}${nextStep.status === "scheduled" ? ` (scheduled ${new Date(nextStep.due_at).toLocaleDateString()})` : ""}`
      : `${sentCount}/${view.messages.length} sent`;

  // The picker's contact list lives in the parent's `prospects` state, so every time a
  // fresh sequence_view comes back from the API, mirror its sequence_status onto that
  // list too — otherwise the picker keeps showing whatever it had at page load.
  const applyView = (v) => {
    setView(v);
    onUpdateProspect(prospectId, {
      sequence_status: v.sequence_status,
      messages_sent: v.messages.filter(m => m.status === "sent").length,
      messages_total: v.messages.length,
    });
    return v;
  };

  const save = async () => {
    setBusy(true); setError("");
    try {
      applyView(await saveMessage(prospectId, current.step_key, { subject, body }));
      toast.success("Draft saved");
    } catch (e) { toast.error("Could not save draft"); } finally { setBusy(false); }
  };

  const revert = async () => {
    setBusy(true);
    try { applyView(await resetMessage(prospectId, current.step_key)); toast.success("Reverted to template"); }
    catch (e) { toast.error("Could not revert"); } finally { setBusy(false); }
  };

  const markSent = async () => {
    setBusy(true); setError("");
    try { applyView(await markMessageSent(prospectId, current.step_key)); toast.success("Marked as sent"); }
    catch (e) { toast.error("Could not update"); } finally { setBusy(false); }
  };

  const saveLinkedinUrl = async () => {
    setBusy(true); setError("");
    try {
      const updated = await setLinkedinUrl(prospectId, linkedinUrlInput.trim());
      onUpdateProspect(prospectId, { linkedin_url: updated.linkedin_url });
      toast.success("LinkedIn URL saved");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save URL");
    } finally { setBusy(false); }
  };

  const connectNow = async () => {
    setBusy(true); setError("");
    try { applyView(await connectLinkedinNow(prospectId)); toast.success("Connection request sent"); }
    catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not connect");
    } finally { setBusy(false); }
  };

  const sendNow = async () => {
    setBusy(true); setError("");
    try { applyView(await sendMessageNow(prospectId, current.step_key, editable ? { subject, body } : {})); toast.success("Email sent"); }
    catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not send email");
    } finally { setBusy(false); }
  };

  const launch = async () => {
    setBusy(true); setError("");
    try { applyView(await launchSequence(prospectId)); toast.success("Sequence launched — emails send automatically within the sending window"); }
    catch (e) {
      const reasons = e?.response?.data?.detail?.reasons;
      setError(Array.isArray(reasons) ? reasons.join(" · ") : "Could not launch sequence");
    } finally { setBusy(false); }
  };

  const stop = async () => {
    setBusy(true);
    try { applyView(await stopSequence(prospectId)); toast.success("Sequence stopped"); }
    catch (e) { toast.error("Could not stop sequence"); } finally { setBusy(false); }
  };

  const resetSeq = async () => {
    setBusy(true);
    try { applyView(await resetSequence(prospectId)); toast.success("Sequence reset — ready to edit and relaunch"); }
    catch (e) { toast.error("Could not reset sequence"); } finally { setBusy(false); }
  };

  const copyMessage = () => {
    navigator.clipboard?.writeText(current.rendered_body || body).then(() => toast.success("Copied to clipboard"));
  };

  return <div className="page-view">
    <button className="back-link" onClick={() => onSelectProspect(null)} data-testid="change-sequence-prospect"
      style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: "#4f6b5d", fontSize: 12, padding: "0 0 12px", margin: 0 }}>
      <ChevronRight size={14} style={{ transform: "rotate(180deg)" }} /> All contacts
    </button>
    <div className="view-toolbar">
      <div><div className="eyebrow"><span className="eyebrow-dot" /> SEQUENCE BUILDER</div><h2>{prospect.company} · {prospect.name}</h2><p>{view.segment_name} segment · {progressLabel}</p></div>
      <div className="toolbar-actions">
        <Badge tone={SEQ_STATUS_TONE[view.sequence_status] || "neutral"}>{view.sequence_status.replace("_", " ")}</Badge>
        {sendStatus && <small style={{ color: "#4f6b5d" }}>Sends {sendStatus.window} {sendStatus.timezone} · {sendStatus.sent_today}/{sendStatus.daily_cap} sent today{sendStatus.timing_problem ? ` · ${sendStatus.timing_problem}` : ""}</small>}
      </div>
    </div>

    {error && <div className="error-strip" data-testid="sequence-error"><AlertCircle size={14} /> {error}</div>}

    <div className="sequence-layout">
      <div className="sequence-steps">
        {view.messages.map((m, i) => <button className={`sequence-step ${selected === i ? "selected" : ""}`} onClick={() => setSelected(i)} key={m.step_key} data-testid={`sequence-step-${i}`}>
          <span className="step-day">{m.day_label}</span>
          <span className={`channel-dot ${m.channel === "email" ? "blue" : "purple"}`} /> <span>{m.channel === "email" ? "EMAIL" : "LINKEDIN"}</span>
          {m.status === "sent" ? <Check size={15} /> : <ChevronRight size={15} />}
        </button>)}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
          {view.sequence_status === "not_started" && <button className="primary-button" onClick={launch} disabled={busy} data-testid="launch-sequence"><Send size={15} /> Launch sequence</button>}
          {view.sequence_status === "active" && <button className="outline-button" onClick={stop} disabled={busy} data-testid="stop-sequence">Stop sequence</button>}
          {["stopped", "replied", "completed", "bounced"].includes(view.sequence_status) && <button className="outline-button" onClick={resetSeq} disabled={busy} data-testid="reset-sequence">Reset sequence</button>}
        </div>
      </div>
      <div className="sequence-editor panel">
        {current && <>
          <div className="editor-head"><div><Badge tone={current.channel === "email" ? "blue" : "purple"}>{current.channel === "email" ? "EMAIL" : "LINKEDIN"}</Badge><h3>{MSG_STATUS_LABEL[current.status] || current.status}</h3></div></div>
          {current.channel === "email" && <label>Subject line <input value={subject} onChange={e => setSubject(e.target.value)} disabled={!editable} data-testid="sequence-subject-input" /></label>}
          <label>Message <textarea value={body} onChange={e => setBody(e.target.value)} disabled={!editable} data-testid="sequence-message-input" /></label>
          {current.channel === "email" && <p style={{ fontSize: 11, color: "#4f6b5d", margin: "0 0 15px" }}>Your signature, opt-out line, and mailing address are added automatically when this sends.</p>}
          {current.channel === "linkedin" && <label>LinkedIn profile URL
            <div style={{ display: "flex", gap: 8 }}>
              <input value={linkedinUrlInput} onChange={e => setLinkedinUrlInput(e.target.value)} placeholder="https://www.linkedin.com/in/…" data-testid="linkedin-url-input" />
              <button className="outline-button" onClick={saveLinkedinUrl} disabled={busy || linkedinUrlInput.trim() === (prospect.linkedin_url || "")} data-testid="save-linkedin-url">Save</button>
            </div>
          </label>}
          {current.problems.length > 0 && <div className="error-strip" data-testid="sequence-step-problems"><AlertCircle size={14} /> {current.problems.join(" · ")}</div>}
          <div className="editor-foot">
            <span><Check size={14} /> {current.status === "sent" ? `Sent ${new Date(current.sent_at).toLocaleString()}`
              : current.status === "scheduled" ? `Scheduled for ${new Date(current.due_at).toLocaleString()}` : "Not sent yet"}</span>
            <div style={{ display: "flex", gap: 8 }}>
              {editable && <button className="outline-button" onClick={save} disabled={busy} data-testid="save-sequence-step"><Check size={15} /> Save draft</button>}
              {editable && current.customized && <button className="outline-button" onClick={revert} disabled={busy} data-testid="revert-sequence-step">Revert to template</button>}
              {current.channel === "email" && ["draft", "scheduled"].includes(current.status) &&
                <button className="primary-button" onClick={sendNow} disabled={busy} data-testid="send-sequence-email"><Send size={15} /> Send now</button>}
              {current.channel === "linkedin" && <>
                <button className="outline-button" onClick={copyMessage} data-testid="copy-linkedin-message">Copy message</button>
                <a className="outline-button" href={prospect.linkedin_url || undefined} target="_blank" rel="noreferrer" aria-disabled={!prospect.linkedin_url} data-testid="open-linkedin-profile">Open LinkedIn</a>
                {config?.linkedin_configured
                  ? <button className="primary-button" onClick={connectNow} disabled={busy || current.status === "sent" || !prospect.linkedin_url} data-testid="connect-linkedin-now" title="Drives a real, visible browser logged in as you to click Connect">{current.status === "sent" ? "Sent" : "Connect"}</button>
                  : <button className="primary-button" onClick={markSent} disabled={busy || current.status === "sent"} data-testid="mark-linkedin-sent">{current.status === "sent" ? "Sent" : "Mark as sent"}</button>}
              </>}
            </div>
          </div>
        </>}
      </div>
    </div>
  </div>;
}


function AccountsView({ accounts, onAdd, onScan, onDelete, scanningId, apolloConfigured }) {
  const view = useListView(accounts, a => `${a.company_name} ${a.domain || ""} ${a.notes || ""} ${a.industry || ""}`, 10);
  return <div className="page-view">
    <div className="view-toolbar">
      <div><div className="eyebrow"><span className="eyebrow-dot" /> TARGET ACCOUNTS</div><h2>Named facilities &amp; intent signals</h2><p>Track specific accounts directly, or fold in a G2/Bombora/Apollo Intent export by hand.</p></div>
      <div className="toolbar-actions">
        <button className="primary-button" onClick={onAdd} data-testid="open-accounts-modal-button"><Plus size={15} /> Add / import</button>
      </div>
    </div>
    {false && (
      <div className="alert-strip" data-testid="accounts-apollo-warning">
        <Sparkles size={15} /><span>Connect Apollo (APOLLO_API_KEY) to scan accounts for hiring and website-visitor signals.</span>
      </div>
    )}
    <div className="full-table panel">
      <ListBar view={view} noun="companies" placeholder="Search company or domain…" />
      <table>
        <thead><tr><th>Company</th><th>Domain</th><th>Signals</th><th>Contacts</th><th /></tr></thead>
        <tbody>
          {accounts.length === 0 && <tr><td colSpan={5} className="empty-row" data-testid="empty-accounts">No companies yet — import your customers, or add one.</td></tr>}
          {view.rows.map(a => <tr key={a.id} data-testid={`account-row-${a.id}`}>
            <td><b>{a.company_name}</b>{a.notes && <small className="table-sub">{a.notes}</small>}</td>
            <td><small className="table-sub">{a.domain || "—"}</small></td>
            <td><div className="signal-list">{(a.signals || []).map(s => <Badge key={s} tone={s === "Hiring" ? "hot" : s === "Topic surge" ? "purple" : s === "Site visit" ? "blue" : "green"}>{s}</Badge>)}{(!a.signals || a.signals.length === 0) && <small className="muted">—</small>}</div></td>
            <td>{a.prospect_count}</td>
            <td className="row-actions">
              {apolloConfigured && <button className="outline-button" title="Scan Apollo for people + signals at this account" onClick={() => onScan(a.id)} disabled={scanningId === a.id || !a.domain} data-testid={`account-scan-${a.id}`}><ScanSearch size={14} /> {scanningId === a.id ? "Scanning…" : "Scan"}</button>}
              <button className="icon-button small" title="Remove" onClick={() => onDelete(a.id)} data-testid={`account-delete-${a.id}`}><Trash2 size={14} /></button>
            </td>
          </tr>)}
        </tbody>
      </table>
    </div>
  </div>;
}

function LocalNewsSignals() {
  const [segments, setSegments] = useState(null);
  const [edits, setEdits] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = () => getSegments().then(setSegments).catch(() => toast.error("Could not load segments"));
  useEffect(() => { load(); }, []);

  const save = async (segmentId) => {
    setBusyId(segmentId);
    try {
      await setSegmentAlertFeed(segmentId, (edits[segmentId] ?? "").trim());
      toast.success("Saved");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save");
    } finally { setBusyId(null); }
  };

  if (!segments) return null;

  return <div className="panel form-panel" style={{ marginTop: 14 }}>
    <h3>Local news signals</h3>
    <p>Create a free Google Alert per segment (e.g. "your city" hiring sales manager), set its delivery to "RSS feed", and paste the feed URL here. Checked once a day — new mentions show up on your To-do list to review; nothing gets added automatically.</p>
    {segments.map(seg => <label key={seg.id}>{seg.name}
      <div style={{ display: "flex", gap: 8 }}>
        <input
          defaultValue={seg.alert_rss_url || ""}
          placeholder="https://www.google.com/alerts/feeds/…"
          onChange={e => setEdits(v => ({ ...v, [seg.id]: e.target.value }))}
          data-testid={`alert-feed-input-${seg.id}`}
        />
        <button className="outline-button" onClick={() => save(seg.id)} disabled={busyId === seg.id} data-testid={`alert-feed-save-${seg.id}`}>Save</button>
      </div>
    </label>)}
  </div>;
}

const TARGET_FIELDS = [
  ["titles", "Buyer job titles", "e.g. Head of Sales, Operations Manager"],
  ["industries", "Industries", "e.g. software, healthcare, logistics"],
  ["locations", "Locations", "e.g. London, UK; Austin, TX"],
  ["employee_ranges", "Company sizes (employee ranges)", "e.g. 11,50; 51,200"],
  ["job_title_keywords", "Hiring signal: job titles they post", "e.g. sales manager, account executive"],
];

function IcpView({ onImport, onDiscover, apolloConfigured, emailConfigured, senderConfigured }) {
  const [target, setTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { getTarget().then(setTarget).catch(() => setTarget({})); }, []);
  const save = async () => {
    setSaving(true);
    try { setTarget(await saveTarget(target)); toast.success("Target customer saved — lead search will start from it"); }
    catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };
  return <div className="page-view">
    <div className="view-toolbar"><div><div className="eyebrow"><span className="eyebrow-dot" /> TARGETING</div><h2>Target customer & sources</h2><p>Describe who you sell to. Lead search starts from these; you can change them for any single search.</p></div><button className="primary-button" onClick={save} disabled={!target || saving} data-testid="save-icp-button"><Check size={15} /> {saving ? "Saving…" : "Save"}</button></div>
    <TargetProfiles />
    <div className="settings-grid">
      <div className="panel form-panel"><h3>Lead search defaults</h3><p>Separate several values with commas (company sizes with semicolons).</p>
        {target && TARGET_FIELDS.map(([k, label, ph]) => <label key={k}>{label}<input value={target[k] || ""} placeholder={ph} onChange={e => setTarget(t => ({ ...t, [k]: e.target.value }))} data-testid={`icp-${k}-input`} /></label>)}
        {target && <label>Notes for your team<input value={target.notes || ""} placeholder="Anything else about who's a good fit" onChange={e => setTarget(t => ({ ...t, notes: e.target.value }))} data-testid="icp-notes-input" /></label>}
      </div>
      <div className="panel sources-panel">
        <h3>Data sources</h3><p>Find new leads, or bring in the customers you already have.</p>
        {apolloConfigured && <div className="source-card"><div className="source-logo apollo">A</div><div><b>Lead search</b><small>{apolloConfigured ? "Connected · Live search" : "Not connected — add a key in Settings"}</small></div><button className="outline-button" data-testid="connect-apollo-button" onClick={onDiscover}>{apolloConfigured ? "Search" : "Connect"}</button></div>}
        <div className="source-card"><div className="source-logo csv"><FileUp size={16} /></div><div><b>Import</b><small>Excel, CSV, Zoho, HubSpot, Salesforce, Pipedrive, Google</small></div><button className="outline-button" data-testid="open-csv-import-button" onClick={onImport}>Import</button></div>
        <div className="source-card"><div className="source-logo mailer"><Send size={16} /></div><div><b>Email (SMTP)</b><small>{emailConfigured ? (senderConfigured ? "Connected · Ready to send" : "Connected · Add sender details in Settings") : "Not connected"}</small></div><span className="muted" style={{ fontSize: 10 }}>Set in Settings</span></div>
      </div>
    </div>
    <LocalNewsSignals />
  </div>;
}


export default function App() {
  const [authed, setAuthed] = useState(!!getToken());
  const [wizardOpen, setWizardOpen] = useState(false);
  const [brand, setBrand] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [brandError, setBrandError] = useState(false);
  const loadBrand = () => getSetupStatus().then((b) => { setBrandError(false); setBrand(b); applyBrand(b, LOGO_URL); }).catch(() => setBrandError(true));
  useEffect(() => { loadBrand(); }, []);
  const [active, setActive] = useState("Overview");
  const [contactQuery, setContactQuery] = useState("");
  const [dialTarget, setDialTarget] = useState(null);
  const [due, setDue] = useState(0);
  const refreshDue = useCallback(() => { followupsCount().then(r => setDue(r.overdue + r.today)).catch(() => {}); }, []);
  useEffect(() => { refreshDue(); const t = setInterval(refreshDue, 60000); return () => clearInterval(t); }, [refreshDue]);
  const callContact = (p) => { setDialTarget(p); setActive("Outbound Sales Calls"); };
  const searchContacts = (v) => { setContactQuery(v); if (v.trim()) setActive("Contacts"); };
  const [prospects, setProspects] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [apolloOpen, setApolloOpen] = useState(false);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [scanningId, setScanningId] = useState(null);
  const [verifyingId, setVerifyingId] = useState(null);
  const [sequenceProspectId, setSequenceProspectId] = useState(null);
  const [sequenceStepKey, setSequenceStepKey] = useState(null);
  const [apolloConfigured, setApolloConfigured] = useState(false);
  const [providerLabel, setProviderLabel] = useState("Apollo");
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [senderConfigured, setSenderConfigured] = useState(false);

  useEffect(() => { setOnUnauthorized(() => setAuthed(false)); }, []);

  useEffect(() => { const h = () => refresh(); window.addEventListener("crm-refresh", h); return () => window.removeEventListener("crm-refresh", h); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = async () => {
    try {
      const list = await listProspects();
      setProspects(list);
    } catch (e) {
      toast.error("Could not load prospects");
    }
  };

  const refreshAccounts = async () => {
    try {
      setAccounts(await listAccounts());
    } catch (e) {
      toast.error("Could not load target accounts");
    }
  };

  const loadConfig = () => getConfig().then(c => {
    setApolloConfigured(!!c.apollo_configured);
    setProviderLabel(c.prospect_provider_label || "Apollo");
    setEmailConfigured(!!c.email_configured);
    setSenderConfigured(!!c.sender_configured);
  }).catch(() => {});

  useEffect(() => {
    if (!authed) return;
    loadConfig();
    refresh();
    refreshAccounts();
  }, [authed]);

  const approve = async (id) => {
    try {
      await approveProspect(id);
      setProspects(ps => ps.map(p => p.id === id ? { ...p, status: "Approved" } : p));
      toast.success("Prospect approved — sequence draft is ready");
    } catch (e) { toast.error("Could not approve"); }
  };

  const remove = async (id) => {
    try {
      await deleteProspect(id);
      setProspects(ps => ps.filter(p => p.id !== id));
      toast.success("Prospect removed");
    } catch (e) { toast.error("Could not delete"); }
  };

  const removeAccount = async (id) => {
    try {
      await deleteAccount(id);
      setAccounts(as => as.filter(a => a.id !== id));
      toast.success("Account removed");
    } catch (e) { toast.error("Could not remove account"); }
  };

  const runScan = async (id) => {
    setScanningId(id);
    try {
      const data = await scanAccount(id, {});
      toast.success(`${data.imported} new prospect(s) found · ${data.duplicates_skipped} duplicates`);
      await Promise.all([refresh(), refreshAccounts()]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Scan failed");
    } finally { setScanningId(null); }
  };

  const verifyEmail = async (id) => {
    setVerifyingId(id);
    try {
      const updated = await verifyProspectEmail(id);
      setProspects(ps => ps.map(p => p.id === id ? { ...p, email: updated.email, email_status: updated.email_status } : p));
      toast.success(updated.email_status === "verified" ? `Verified: ${updated.email}` : `Email status: ${updated.email_status}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not verify email");
    } finally { setVerifyingId(null); }
  };

  const addOrUpdateProspect = (p) => setProspects(ps => ps.some(x => x.id === p.id) ? ps.map(x => x.id === p.id ? { ...x, ...p } : x) : [{ ...p, messages_sent: 0, messages_total: 5 }, ...ps]);

  const patchProspect = (id, patch) => setProspects(ps => ps.map(p => p.id === id ? { ...p, ...patch } : p));

  const openImport = () => setActive("Import");
  const openDiscover = () => setApolloOpen(true);
  const openAccounts = () => setAccountsOpen(true);
  const logout = () => { clearToken(); setAuthed(false); };

  const openTodoTask = (prospectId, stepKey) => {
    setSequenceProspectId(prospectId);
    setSequenceStepKey(stepKey || null);
    setActive("Sequences");
  };

  const content = useMemo(() => {
    if (active === "Contacts") return <ProspectsView onCall={callContact} onRefresh={refresh} prospects={prospects} onApprove={approve} onDelete={remove} onImport={openImport} onDiscover={openDiscover} onVerify={verifyEmail} verifyingId={verifyingId} onOpenSequence={(id) => { setSequenceProspectId(id); setActive("Sequences"); }} apolloConfigured={apolloConfigured} onUpdateProspect={patchProspect} query={contactQuery} setQuery={setContactQuery} onAddProspect={addOrUpdateProspect} query={contactQuery} setQuery={setContactQuery} />;
    if (active === "Companies") return <AccountsView accounts={accounts} onAdd={openAccounts} onScan={runScan} onDelete={removeAccount} scanningId={scanningId} apolloConfigured={apolloConfigured} onLog={callContact} />;
    if (active === "To-do") return <TodoView onOpenTask={openTodoTask} onOpenCalls={() => setActive("Calls")} onOpenSocial={() => setActive("Social")} />;
    if (active === "Calls") return <CallsView />;
    if (active === "Support Desk") return <SupportDesk onCall={callContact} />;
    if (active === "Outbound Sales Calls") return <SalesCalls prospects={prospects} target={dialTarget} clearTarget={() => setDialTarget(null)} repName={brand.user_name} onFollowupsChanged={refreshDue} />;
    if (active === "Social") return <SocialView />;
    if (active === "Sequences") return <SequencesView prospects={prospects} prospectId={sequenceProspectId} initialStepKey={sequenceStepKey} onSelectProspect={setSequenceProspectId} onUpdateProspect={patchProspect} />;
    if (active === "Analytics") return <AnalyticsView />;
    if (active === "Import") return <ImportView onImported={() => { refreshAccounts(); refresh(); }} />;
    if (active === "Targeting") return <IcpView onImport={openImport} onDiscover={openDiscover} apolloConfigured={apolloConfigured} emailConfigured={emailConfigured} senderConfigured={senderConfigured} />;
    return <Overview prospects={prospects} onApprove={approve} setActive={setActive} onDiscover={openDiscover} apolloConfigured={apolloConfigured} onLog={callContact} />;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, contactQuery, prospects, accounts, scanningId, verifyingId, sequenceProspectId, sequenceStepKey, apolloConfigured, emailConfigured, senderConfigured]);

  if (brandError) return <div className="login-shell"><div className="modal login-card"><h2>Can't reach the CRM engine</h2><p>Close and reopen the app. If this keeps happening, the engine may have stopped.</p><button className="primary-button full" onClick={loadBrand}>Try again</button></div></div>;
  if (!brand) return null;
  if (!brand.configured) return <><Setup onDone={() => { loadBrand(); setAuthed(true); setWizardOpen(true); }} /><Toaster theme="dark" position="bottom-right" /></>;
  const linkToken = new URLSearchParams(window.location.search).get("set-password");
  if (linkToken) return <><SetPassword token={linkToken} onDone={() => setAuthed(true)} /><Toaster theme="dark" position="bottom-right" /></>;
  if (!authed) return <><Login brand={brand} onSuccess={() => setAuthed(true)} /><Toaster theme="dark" position="bottom-right" /></>;

  return <div className="app-shell">
    <Sidebar active={active} setActive={setActive} onLogout={logout} brand={brand} due={due} onSettings={() => setSettingsOpen(true)} />
    <main className="main">
      <Topbar search={contactQuery} onSearch={searchContacts} active={active} onImport={openImport} onDiscover={openDiscover} apolloConfigured={apolloConfigured} brand={brand} />
      <div className="content">{content}</div>
    </main>
    {wizardOpen && <SetupWizard close={() => { setWizardOpen(false); loadConfig(); refresh(); }} />}
    {settingsOpen && <SettingsModal close={() => { setSettingsOpen(false); loadConfig(); refresh(); loadBrand(); }} />}
    {apolloOpen && <ApolloDiscoveryModal close={() => setApolloOpen(false)} onImported={refresh} apolloConfigured={apolloConfigured} providerLabel={providerLabel} />}
    {accountsOpen && <AccountsModal close={() => setAccountsOpen(false)} onChanged={() => { refreshAccounts(); refresh(); }} />}
    <Toaster theme="dark" position="bottom-right" />
  </div>;
}
